import {
  ArticleList,
} from "@babackapp/customer-ui/components/article";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { Separator } from "@babackapp/ui/components/separator";
import { EnterIcon } from "@radix-ui/react-icons";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useRemoveLineItem } from "@babackapp/return-widget-components/hooks/use-remove-line-item";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { PlusIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useWidgetParams } from "../hooks/use-widget-params";
import { SummaryLineItem } from "./summary-line-item";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";

export function ReturnRequestSummary() {
  const { orderId, shopId, shopSlug, logoUrl } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const navigate = useNavigate();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const summary = useFinancialSummary(orderId, order ?? null);
  const removeLineItem = useRemoveLineItem(orderId);
  const returnableItems = useReturnableItems(orderId, shopSlug);
  const reasons = useReturnReasons(shopId);
  const t = useCustomText();
  const draftLineItemIds = new Set(draft?.lineItems.map((li) => li.lineItemId) ?? []);
  const hasMoreReturnableItems = returnableItems.some(
    (item) => !draftLineItemIds.has(item.line_item_id),
  );

  const retractationReasonIds = new Set(
    reasons.filter((r) => r.isRetractation).map((r) => r.id),
  );
  const isRetractationItem = (reasonId: string | null) =>
    reasonId !== null && retractationReasonIds.has(reasonId);

  const handleRemove = (lineItemId: string) => {
    const currentCount = draft?.lineItems.length ?? 0;
    removeLineItem(lineItemId);
    if (currentCount <= 1) {
      navigate(`/return-widget/${shopId}/order/${orderId}/line-item`);
    }
  };

  const exchangeItems =
    draft?.lineItems.filter(
      (li) => li.resolution === "exchange" || li.resolution === "exchange-credit",
    ) ?? [];
  const refundItems = draft?.lineItems.filter((li) => li.resolution === "refund") ?? [];
  const discountItems = draft?.lineItems.filter((li) => li.resolution === "discount") ?? [];
  const orderLineItems = order?.lineItems ?? [];

  return (
    <WidgetPage>
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>{t("summary.title")}</HeaderTitle>
        <HeaderDescription>
          {t("summary.description")}
        </HeaderDescription>
      </Header>

      <UserPortalCallout texts={shop?.preferences?.userPortal?.confirmationMessage} />

      <div className="w-full mt-4 flex flex-col gap-6">
        {exchangeItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("summary.exchangeItems")}</SectionHeader>
            <ArticleList size="sm">
              {exchangeItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={orderLineItems}
                  order={order ?? null}
                  isRetractation={isRetractationItem(item.reasonId)}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}
        {refundItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("summary.refundItems")}</SectionHeader>
            <ArticleList size="sm">
              {refundItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={orderLineItems}
                  order={order ?? null}
                  isRetractation={isRetractationItem(item.reasonId)}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}
        {discountItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("summary.discountItems")}</SectionHeader>
            <ArticleList size="sm">
              {discountItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={orderLineItems}
                  order={order ?? null}
                  isRetractation={isRetractationItem(item.reasonId)}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}
        <Separator />

        {summary.refundTotal > 0 && (
          <ReceiptLine>
            <ReceiptLineLabel>{t("financial.refundTotal")}</ReceiptLineLabel>
            <ReceiptLineValue>{formatPrice(summary.refundTotal, summary.currency)}</ReceiptLineValue>
          </ReceiptLine>
        )}
        {summary.discountTotal > 0 && (
          <ReceiptLine>
            <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
            <ReceiptLineValue>{formatPrice(summary.discountTotal, summary.currency)}</ReceiptLineValue>
          </ReceiptLine>
        )}
      </div>

      <AnimatedButtonRow variants={{}}>
        {hasMoreReturnableItems && (
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ enter: true, exit: true }}
            layout="position"
          >
            <Link to="../line-item">
              <Button variant="secondary">
                <PlusIcon />
                {t("common.addAnotherItem")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
        )}
        <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
          <Link to={summary.exchangeCreditTotal > 0 ? "../shop-now" : "../return-mode"}>
            <Button>
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </Link>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
